// Live vendor-native per-domain analytics — pulled straight from the CDN
// vendor (vEdge/Conversant) via the mgmt passthrough, NOT the central store.
//
//   GET /cdn/domains/:id/analytics/:metric   fan-out across every binding
//
// The central analytics store only keeps domain-level aggregates (country +
// status dims), so URL-granular data like top-paths has no cheaper source and
// must be pulled live. These hooks power the opt-in Service Overview cards, so
// they default disabled (`enabled: false`) to avoid extra vendor traffic.
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope } from './_shared';

/** One vendor-native top-path row (vEdge `TopPathRow`). */
export interface TopPathRow {
  path: string;
  requests: number;
  bandwidth: number;
  avg_duration_ms: number;
  cache_hit_ratio: number;
  error_count: number;
}

/** Merged, provider-agnostic entry rendered in the card. */
export interface TopPathEntry {
  path: string;
  requests: number;
  bandwidth: number;
}

/** vEdge accepts these window tokens natively; anything else must go through
 *  the `period=custom` + from/to path. */
const NATIVE_PERIODS = new Set(['24h', '7d', '30d']);
const CUSTOM_TS_LAYOUT = (iso: string) => new Date(iso).toISOString().slice(0, 19).replace('T', ' ');

/** Build the vendor query params for a given UI period. Native tokens pass
 *  through; month presets fall back to a custom from/to range. */
function periodParams(period: string, from?: string, to?: string): URLSearchParams {
  const p = new URLSearchParams();
  if (NATIVE_PERIODS.has(period) || !from || !to) {
    p.set('period', period);
  } else {
    p.set('period', 'custom');
    p.set('from', CUSTOM_TS_LAYOUT(from));
    p.set('to', CUSTOM_TS_LAYOUT(to));
  }
  return p;
}

/** Fan-out response is provider-keyed (`{ raven: [...], eagle: [...] }`). Merge
 *  by path, summing requests + bandwidth so a domain served by multiple vendors
 *  shows one combined ranking. Bad/absent binding values are skipped. */
function mergeTopPaths(raw: Record<string, unknown> | null | undefined, limit: number): TopPathEntry[] {
  if (!raw) return [];
  const acc = new Map<string, TopPathEntry>();
  for (const val of Object.values(raw)) {
    if (!Array.isArray(val)) continue; // ErrUnsupported / error entries come back non-array
    for (const row of val as TopPathRow[]) {
      if (!row?.path) continue;
      const cur = acc.get(row.path) ?? { path: row.path, requests: 0, bandwidth: 0 };
      cur.requests += Number(row.requests) || 0;
      cur.bandwidth += Number(row.bandwidth) || 0;
      acc.set(row.path, cur);
    }
  }
  return [...acc.values()].sort((a, b) => b.requests - a.requests).slice(0, limit);
}

/** Live top URLs/paths for one CDN domain across all its bindings. */
export function useDomainTopPaths(
  domainId: string | undefined,
  opts: { period: string; from?: string; to?: string; limit?: number; enabled?: boolean },
) {
  const { period, from, to, limit = 15, enabled = true } = opts;
  return useQuery({
    queryKey: ['cdn', 'domain-top-paths', domainId ?? '', period, from ?? '', to ?? '', limit],
    enabled: enabled && !!domainId,
    retry: false,
    queryFn: async (): Promise<TopPathEntry[]> => {
      const p = periodParams(period, from, to);
      p.set('limit', String(limit));
      try {
        const res = await serviceApiV3.get<Envelope<Record<string, TopPathRow[]>>>(
          `/cdn/domains/${domainId}/analytics/top-paths?${p.toString()}`,
        );
        return mergeTopPaths(res.data?.data, limit);
      } catch {
        return [];
      }
    },
  });
}
